import { createContext, useContext, useEffect, ReactNode, useMemo } from 'react';
import { User } from '@supabase/supabase-js';

import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { getCurrentUser, setUser } from '../../features/auth/authSlice';
import { authService } from '../services/auth';
import { supabase } from '../libs/supabase';
import { ensureUserRow } from '../utils/ensureUserRow';

async function ensurePublicUser(user: User) {
  try {
    await ensureUserRow(supabase, user);
  } catch (err: any) {
    console.error('ensurePublicUser failed:', err.message);
  }
}

interface AuthContextType {
  user: User | null;
  initializing: boolean;
  loading: boolean;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const dispatch = useAppDispatch();
  const { user, initializing, loading, isAuthenticated } = useAppSelector((state) => state.auth);

  useEffect(() => {
    void dispatch(getCurrentUser())
      .unwrap()
      .then((currentUser) => {
        if (currentUser) {
          void ensurePublicUser(currentUser);
        }
      })
      .catch(() => undefined);

    const {
      data: { subscription },
    } = authService.onAuthStateChange((_event, session) => {
      const currentUser = session?.user ?? null;
      dispatch(setUser(currentUser));
      if (currentUser) {
        void ensurePublicUser(currentUser);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [dispatch]);

  const value = useMemo(
    () => ({ user, initializing, loading, isAuthenticated }),
    [user, initializing, loading, isAuthenticated]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
